import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { updatePassword, logoutUser } from '../../redux/actions/userActions';
import { BOARD_THEMES } from '../../components/WorkingChessLogic/BoardStyle.js';
import { Chessboard } from 'react-chessboard';
import { usePremium } from '../../util/PremiumFilter';
import { isNativeApp } from '../../util/platformDetection';
import { getAuth, deleteUser, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../../firebase';

const MobileSettings = ({ user, updatePassword, logoutUser, UI }) => {
  const history = useHistory();
  const { renderUserWithBadge } = usePremium();

  // Board theme state with localStorage
  const [boardTheme, setBoardTheme] = useState(() => {
    return localStorage.getItem('boardTheme') || 'blue';
  });

  // Custom colors state with localStorage
  const [customColors, setCustomColors] = useState(() => {
    const savedCustomColors = localStorage.getItem('customColors');
    return savedCustomColors ? JSON.parse(savedCustomColors) : {
      dark: '#3066be',
      light: '#eeeed2'
    };
  });

  // Coordinates state with localStorage
  const [showCoordinates, setShowCoordinates] = useState(() => {
    const savedCoordinates = localStorage.getItem('showCoordinates');
    return savedCoordinates === null ? true : savedCoordinates === 'true';
  });

  // Password update state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteStep, setDeleteStep] = useState(1); // 1: warning, 2: password confirmation
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle theme change
  const handleThemeChange = (newTheme) => {
    setBoardTheme(newTheme);
    localStorage.setItem('boardTheme', newTheme);
  };

  // Handle custom color change
  const handleCustomColorChange = (type, value) => {
    const newCustomColors = { ...customColors, [type]: value };
    setCustomColors(newCustomColors);
    localStorage.setItem('customColors', JSON.stringify(newCustomColors));
    
    if (boardTheme !== 'custom') {
      setBoardTheme('custom');
      localStorage.setItem('boardTheme', 'custom');
    }
  };

  // Handle coordinates change
  const handleCoordinatesChange = (checked) => {
    setShowCoordinates(checked);
    localStorage.setItem('showCoordinates', checked);
  };

  // Handle password form change
  const handlePasswordFormChange = (field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle password update
  const handlePasswordUpdate = (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!passwordForm.currentPassword) errors.currentPassword = 'Current password is required';
    if (!passwordForm.newPassword) errors.newPassword = 'New password is required';
    if (passwordForm.newPassword && passwordForm.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    updatePassword(
      { 
        currentPassword: passwordForm.currentPassword, 
        newPassword: passwordForm.newPassword 
      },
      () => {
        setPasswordUpdateSuccess(true);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordUpdateSuccess(false);
        }, 2000);
      },
      (err) => {
        if (err.code === 'auth/wrong-password') {
          setPasswordErrors({ currentPassword: 'Current password is incorrect' });
        } else if (err.code === 'auth/weak-password') {
          setPasswordErrors({ newPassword: 'Password is too weak' });
        } else {
          setPasswordErrors({ general: 'Error updating password. Please try again.' });
        }
      }
    );
  };

  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordErrors({});
    setPasswordUpdateSuccess(false);
  };

  // Delete account handlers
  const openDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteStep(1);
    setDeletePassword('');
    setDeleteError('');
    setIsDeleting(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }

    if (!deletePassword) {
      setDeleteError('Please enter your password to confirm');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser || !currentUser.email) {
        setDeleteError('Unable to verify user. Please try logging out and back in.');
        setIsDeleting(false);
        return;
      }

      // Step 1: Reauthenticate user with password
      const credential = EmailAuthProvider.credential(currentUser.email, deletePassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Step 2: Delete user document from Firestore
      try {
        const userDocRef = doc(firestore, 'users', currentUser.uid);
        await deleteDoc(userDocRef);
      } catch (firestoreError) {
        console.error('Error deleting user document:', firestoreError);
        // Continue with auth deletion even if Firestore fails
      }

      // Step 3: Delete Firebase Auth user
      await deleteUser(currentUser);

      // Step 4: Clear Redux state
      logoutUser();

      // Step 5: Clear localStorage
      localStorage.clear();
      
      // Step 6: Redirect to login
      history.push('/login');
      
    } catch (error) {
      console.error('Error deleting account:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/wrong-password') {
        setDeleteError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setDeleteError('Too many attempts. Please try again later.');
      } else if (error.code === 'auth/requires-recent-login') {
        setDeleteError('Please log out and log back in, then try again.');
      } else {
        setDeleteError('Error deleting account. Please try again.');
      }
      
      setIsDeleting(false);
    }
  };

  return (
    <div style={{
      width: '100vw',
      height: 'calc(100dvh - 55px)', // Account for bottom navigation
      backgroundColor: '#000',
      display: 'flex',
      paddingTop: 'env(safe-area-inset-top)', // Header only in native app
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '15px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h2 style={{
          color: 'gold',
          margin: 0,
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Settings
        </h2>
        <button
          onClick={() => history.goBack()}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#888',
            fontSize: '28px',
            cursor: 'pointer',
            padding: '5px'
          }}
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '20px',
        paddingBottom: 'env(safe-area-inset-bottom)' // iPhone gesture area
      }}>
        {/* Chessboard Section */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #333',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px',
            borderBottom: '1px solid #333'
          }}>
            <h3 style={{
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold',
              margin: 0
            }}>
              Chessboard
            </h3>
            <div style={{
              width: '60px',
              height: '60px',
              overflow: 'hidden',
              borderRadius: '4px',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                transform: 'scale(4)',
                transformOrigin: 'top left'
              }}>
                <Chessboard
                  boardWidth={60}
                  customDarkSquareStyle={boardTheme === 'custom' 
                    ? { background: customColors.dark } 
                    : BOARD_THEMES[boardTheme].dark}
                  customLightSquareStyle={boardTheme === 'custom' 
                    ? { background: customColors.light } 
                    : BOARD_THEMES[boardTheme].light}
                  position="8/8/8/8/8/8/8/8 w - - 0 1"
                  showBoardNotation={false}
                  arePiecesDraggable={false}
                />
              </div>
            </div>
          </div>

          <div style={{ padding: '20px' }}>
            {/* Board Theme */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <label style={{ color: 'white', fontSize: '16px' }}>Board Theme</label>
              <select 
                value={boardTheme}
                onChange={(e) => handleThemeChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#333',
                  border: '1px solid #555',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: '14px'
                }}
              >
                {Object.keys(BOARD_THEMES).filter(theme => theme !== 'custom').map(theme => (
                  <option key={theme} value={theme}>
                    {theme.charAt(0).toUpperCase() + theme.slice(1)}
                  </option>
                ))}
                <option value="custom">Custom Colors</option>
              </select>
            </div>

            {/* Custom Colors */}
            {boardTheme === 'custom' && (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <label style={{ color: 'white', fontSize: '16px' }}>Dark Squares</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      backgroundColor: customColors.dark,
                      borderRadius: '4px',
                      border: '1px solid #555',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <input 
                        type="color" 
                        value={customColors.dark}
                        onChange={(e) => handleCustomColorChange('dark', e.target.value)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                    <input 
                      type="text" 
                      value={customColors.dark}
                      onChange={(e) => handleCustomColorChange('dark', e.target.value)}
                      style={{
                        padding: '6px 8px',
                        backgroundColor: '#ffffff',
                        border: '2px solid #ccc',
                        borderRadius: '8px',
                        color: '#000000',
                        fontSize: '14px',
                        width: '70px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <label style={{ color: 'white', fontSize: '16px' }}>Light Squares</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      backgroundColor: customColors.light,
                      borderRadius: '4px',
                      border: '1px solid #555',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <input 
                        type="color" 
                        value={customColors.light}
                        onChange={(e) => handleCustomColorChange('light', e.target.value)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                    <input 
                      type="text" 
                      value={customColors.light}
                      onChange={(e) => handleCustomColorChange('light', e.target.value)}
                      style={{
                        padding: '6px 8px',
                        backgroundColor: '#ffffff',
                        border: '2px solid #ccc',
                        borderRadius: '8px',
                        color: '#000000',
                        fontSize: '14px',
                        width: '70px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </>
            )}

          </div>
        </div>

        {/* Account Section */}
        <div style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #333',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #333'
          }}>
            <h3 style={{
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold',
              margin: 0
            }}>
              Account
            </h3>
          </div>

          <div style={{ padding: '20px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <div>
                <div style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '5px'
                }}>
                  {renderUserWithBadge(user.credentials?.handle || 'Username')}
                </div>
                <div style={{
                  color: '#888',
                  fontSize: '14px'
                }}>
                  {user.credentials?.email || 'email@example.com'}
                </div>
              </div>
            </div>

            <button 
              onClick={openPasswordModal}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'transparent',
                border: '2px solid #666',
                color: '#ccc',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginBottom: '15px'
              }}
            >
              Change Password
            </button>

            <button 
              onClick={openDeleteModal}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'transparent',
                border: '2px solid #ff0050',
                color: '#ff0050',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Delete Account
            </button>
          </div>
        </div>

      </div>

      {/* Password Update Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '2px solid #666',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%'
          }}>
            {passwordUpdateSuccess ? (
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ color: '#ccc', marginBottom: '15px' }}>Success!</h3>
                <p style={{ color: 'white', margin: 0 }}>
                  Your password has been updated successfully.
                </p>
              </div>
            ) : (
              <>
                <h3 style={{
                  color: '#ccc',
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  Change Password
                </h3>

                <form onSubmit={handlePasswordUpdate} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px'
                }}>
                  <div>
                    <input
                      type="password"
                      placeholder="Current Password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => handlePasswordFormChange('currentPassword', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: '#ffffff',
                        border: passwordErrors.currentPassword ? '2px solid #ff0050' : '2px solid #ccc',
                        borderRadius: '8px',
                        color: '#000000',
                        fontSize: '16px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {passwordErrors.currentPassword && (
                      <div style={{ color: '#ff0050', fontSize: '14px', marginTop: '5px' }}>
                        {passwordErrors.currentPassword}
                      </div>
                    )}
                  </div>

                  <div>
                    <input
                      type="password"
                      placeholder="New Password"
                      value={passwordForm.newPassword}
                      onChange={(e) => handlePasswordFormChange('newPassword', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: '#ffffff',
                        border: passwordErrors.newPassword ? '2px solid #ff0050' : '2px solid #ccc',
                        borderRadius: '8px',
                        color: '#000000',
                        fontSize: '16px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {passwordErrors.newPassword && (
                      <div style={{ color: '#ff0050', fontSize: '14px', marginTop: '5px' }}>
                        {passwordErrors.newPassword}
                      </div>
                    )}
                  </div>

                  <div>
                    <input
                      type="password"
                      placeholder="Confirm New Password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => handlePasswordFormChange('confirmPassword', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: '#ffffff',
                        border: passwordErrors.confirmPassword ? '2px solid #ff0050' : '2px solid #ccc',
                        borderRadius: '8px',
                        color: '#000000',
                        fontSize: '16px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {passwordErrors.confirmPassword && (
                      <div style={{ color: '#ff0050', fontSize: '14px', marginTop: '5px' }}>
                        {passwordErrors.confirmPassword}
                      </div>
                    )}
                  </div>

                  {passwordErrors.general && (
                    <div style={{
                      color: '#ff0050',
                      fontSize: '14px',
                      textAlign: 'center',
                      backgroundColor: 'rgba(255, 0, 80, 0.1)',
                      padding: '10px',
                      borderRadius: '6px'
                    }}>
                      {passwordErrors.general}
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    marginTop: '15px'
                  }}>
                    <button
                      type="button"
                      onClick={() => setShowPasswordModal(false)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: 'transparent',
                        border: '2px solid #666',
                        color: '#666',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: 'transparent',
                        border: '2px solid #888',
                        color: '#ccc',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      Change
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '2px solid #ff0050',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%'
          }}>
            {deleteStep === 1 ? (
              <>
                <h3 style={{
                  color: '#ff0050',
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  ⚠️&nbsp;Delete Account
                </h3>
                
                <div style={{
                  color: 'white',
                  marginBottom: '20px',
                  lineHeight: '1.5'
                }}>
                  <p>This action will permanently delete your account and cannot be undone.</p>
                
                </div>

                <div style={{
                  display: 'flex',
                  gap: '10px'
                }}>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: 'transparent',
                      border: '2px solid #666',
                      color: '#666',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: 'transparent',
                      border: '2px solid #ff0050',
                      color: '#ff0050',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{
                  color: '#ff0050',
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  Password Confirmation
                </h3>
                
                <div style={{
                  color: 'white',
                  marginBottom: '20px',
                  lineHeight: '1.5'
                }}>
                  <p>Enter your password to permanently delete your account:</p>
                </div>

                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: '#ffffff',
                    border: deleteError ? '2px solid #ff0050' : '2px solid #ccc',
                    borderRadius: '8px',
                    color: '#000000',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    marginBottom: '15px'
                  }}
                />

                {deleteError && (
                  <div style={{
                    color: '#ff0050',
                    fontSize: '14px',
                    textAlign: 'center',
                    marginBottom: '15px'
                  }}>
                    {deleteError}
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: '10px'
                }}>
                  <button
                    onClick={() => setDeleteStep(1)}
                    disabled={isDeleting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: 'transparent',
                      border: '2px solid #666',
                      color: '#666',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: isDeleting ? 'default' : 'pointer',
                      opacity: isDeleting ? 0.5 : 1
                    }}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: isDeleting ? '#ff0050' : 'transparent',
                      border: '2px solid #ff0050',
                      color: isDeleting ? 'white' : '#ff0050',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: isDeleting ? 'default' : 'pointer',
                      opacity: isDeleting ? 0.7 : 1
                    }}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Account'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  UI: state.UI
});

const mapDispatchToProps = {
  updatePassword,
  logoutUser
};

export default connect(mapStateToProps, mapDispatchToProps)(MobileSettings);